import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Flex,
  useColorModeValue,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { loggedInUserState } from "atoms/loggedInUserState";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import PayeesTable from "./PayeesTable";
import PayeesSummary from "./PayeesSummary";

// Motion components
const MotionBox = motion(Box);

const PayeesList = () => {
  const loggedInUser = useRecoilValue(loggedInUserState);
  const setToast = useSetRecoilState(toastState);
  const [payeesData, setPayeesData] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState("all");

  // Theme colors
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("gray.50", "gray.900");

  const [{ data, loading, error }, execute, cancelRequest] = useAxios(
    {
      url: `/api/user/payees`,
      method: "GET",
    },
    { manual: true }
  );
  const fetchPayeesData = async () => {
    try {
      const response = await execute();
      const responseData = response.data.data || [];
      setPayeesData(responseData);
      logger.log("Payees data fetched:", responseData);
    } catch (error) {
      logger.error("Error fetching payees data:", error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occurred",
          status: "error",
          description: error?.response?.data?.error || "Failed to fetch payees data",
        });
      }
    }
  };

  useEffect(() => {
    fetchPayeesData();
    return () => cancelRequest();
  }, []);

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="orange.500" thickness="4px" />
          <Text color={textColor}>Loading payees data...</Text>
        </VStack>
      </Center>
    );
  }

  // if (error) {
  //   return (
  //     <Box p={6}>
  //       <Alert status="error" borderRadius="md">
  //         <AlertIcon />
  //         Failed to load payees data. Please try again later.
  //       </Alert>
  //     </Box>
  //   );
  // }

  return (
    <Box
      px={{ base: 4, md: 6 }}
      bg={bgColor}
      minH="100vh"
    >
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        w="100%"
        maxW="8xl"
        mx="auto"
      >
        <VStack spacing={6} w="100%" align="stretch">
          {/* Header Section */}
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <Box>
              <Heading
                size="lg"
                color={textColor}
                fontWeight="bold"
                letterSpacing="tight"
              >
                Payees Management
              </Heading>
              <Text color="gray.500" fontSize="md" mt={1}>
                Manage and monitor all payee accounts and their verification status
              </Text>
            </Box>
          </Flex>

          {/* Summary Cards */}
          <PayeesSummary payeesData={payeesData} />          {/* Table Section */}
          <PayeesTable
            tableData={payeesData}
            onRefresh={fetchPayeesData}
            statusFilter={verificationStatus}
            setStatusFilter={setVerificationStatus}
          />
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default PayeesList;
